import React, { useState } from 'react';
import {
  Stack,
  Group,
  Button,
  Modal,
  TextInput,
  Select,
  ActionIcon,
  Paper,
  Text,
  Badge,
  Flex,
  Menu,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash, IconGripVertical, IconDots } from '@tabler/icons-react';
import { PropertyDefinition, PropertyType, createProperty } from '../../../types/models/Property';

interface PropertyManagerProps {
  properties: PropertyDefinition[];
  onPropertiesChange: (properties: PropertyDefinition[]) => void;
}

const PROPERTY_TYPES: { value: PropertyType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'status', label: 'Status' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
];

/**
 * Property Manager Component
 * Allows adding, editing, deleting, and reordering properties
 */
export const PropertyManager: React.FC<PropertyManagerProps> = ({
  properties,
  onPropertiesChange,
}) => {
  const [isAddingProperty, setIsAddingProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState<PropertyDefinition | null>(null);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyType, setNewPropertyType] = useState<PropertyType>('text');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Handle add property
  const handleAddProperty = () => {
    if (!newPropertyName.trim()) return;

    const newProperty = createProperty(
      `prop_${Date.now()}`,
      newPropertyName,
      newPropertyType,
      properties.length
    );

    onPropertiesChange([...properties, newProperty]);
    setIsAddingProperty(false);
    setNewPropertyName('');
    setNewPropertyType('text');
  };

  // Handle edit property
  const handleEditProperty = (property: PropertyDefinition) => {
    setEditingProperty(property);
  };

  // Handle save edited property
  const handleSaveEditedProperty = (property: PropertyDefinition) => {
    onPropertiesChange(
      properties.map(p => (p.id === property.id ? property : p))
    );
    setEditingProperty(null);
  };

  // Handle delete property
  const handleDeleteProperty = (propertyId: string) => {
    if (confirm('Delete this property?')) {
      onPropertiesChange(properties.filter(p => p.id !== propertyId));
    }
  };

  // Handle property reorder
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newProperties = [...properties];
    const [draggedProperty] = newProperties.splice(draggedIndex, 1);
    newProperties.splice(targetIndex, 0, draggedProperty);

    // Update positions
    newProperties.forEach((prop, idx) => {
      prop.position = idx;
    });

    onPropertiesChange(newProperties);
    setDraggedIndex(null);
  };

  // Handle toggle visibility
  const handleToggleVisibility = (propertyId: string) => {
    onPropertiesChange(
      properties.map(p =>
        p.id === propertyId ? { ...p, visible: !p.visible } : p
      )
    );
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text fw={500}>Database Properties</Text>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={() => setIsAddingProperty(true)}
        >
          Add Property
        </Button>
      </Group>

      <Stack gap="sm">
        {properties.length === 0 ? (
          <Text c="dimmed" size="sm">
            No properties yet. Add one to get started.
          </Text>
        ) : (
          properties.map((property, index) => (
            <Paper
              key={property.id}
              p="sm"
              radius="sm"
              withBorder
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              style={{
                opacity: draggedIndex === index ? 0.5 : 1,
                cursor: 'grab',
              }}
            >
              <Flex gap="sm" align="center">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  style={{ cursor: 'grab' }}
                >
                  <IconGripVertical size={14} />
                </ActionIcon>

                <Stack gap={0} style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {property.name}
                  </Text>
                  <Badge size="sm" variant="light">
                    {property.type}
                  </Badge>
                </Stack>

                <Group gap={4}>
                  <Menu shadow="md">
                    <Menu.Target>
                      <ActionIcon size="sm" variant="subtle">
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEdit size={14} />}
                        onClick={() => handleEditProperty(property)}
                      >
                        Edit
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => handleToggleVisibility(property.id)}
                      >
                        {property.visible ? 'Hide' : 'Show'}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => handleDeleteProperty(property.id)}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Flex>
            </Paper>
          ))
        )}
      </Stack>

      {/* Add Property Modal */}
      <Modal
        opened={isAddingProperty}
        onClose={() => {
          setIsAddingProperty(false);
          setNewPropertyName('');
          setNewPropertyType('text');
        }}
        title="Add Property"
        size="sm"
      >
        <Stack>
          <TextInput
            label="Property Name"
            placeholder="e.g., Status, Priority"
            value={newPropertyName}
            onChange={(e) => setNewPropertyName(e.currentTarget.value)}
          />
          <Select
            label="Property Type"
            placeholder="Select type"
            data={PROPERTY_TYPES}
            value={newPropertyType}
            onChange={(val) => setNewPropertyType(val as PropertyType)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => {
                setIsAddingProperty(false);
                setNewPropertyName('');
                setNewPropertyType('text');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddProperty} disabled={!newPropertyName.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Property Modal */}
      {editingProperty && (
        <Modal
          opened={!!editingProperty}
          onClose={() => setEditingProperty(null)}
          title="Edit Property"
          size="sm"
        >
          <Stack>
            <TextInput
              label="Property Name"
              value={editingProperty.name}
              onChange={(e) =>
                setEditingProperty({
                  ...editingProperty,
                  name: e.currentTarget.value,
                })
              }
            />
            <Text size="sm" c="dimmed">
              Type: {editingProperty.type}
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setEditingProperty(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleSaveEditedProperty(editingProperty)}>
                Save
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Stack>
  );
};

export default PropertyManager;
